import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import CategoryGrid from './components/CategoryGrid';
import ArticleSection from './components/ArticleSection';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const trustBadges = [
  {
    icon: 'ri-time-line',
    label: 'Updated Daily',
    desc: 'Fresh finds added every 24 hours',
    image: 'https://readdy.ai/api/search-image?query=Calendar%20and%20clock%20on%20a%20clean%20warm%20cream%20desk%20surface%20with%20natural%20morning%20light%20soft%20shadows%20suggesting%20daily%20routine%20and%20organization%20minimalist%20aesthetic%20editorial%20style%20warm%20neutral%20tones%20clean%20composition%20high%20detail&width=400&height=300&seq=trust-badge-daily-v2&orientation=landscape',
  },
  {
    icon: 'ri-shield-check-line',
    label: 'QC Verified',
    desc: 'Quality checked by community',
    image: 'https://readdy.ai/api/search-image?query=Magnifying%20glass%20examining%20fabric%20texture%20detail%20on%20a%20warm%20cream%20surface%20soft%20natural%20lighting%20highlighting%20material%20quality%20inspection%20concept%20minimalist%20product%20photography%20clean%20composition%20neutral%20background%20editorial%20style%20high%20detail&width=400&height=300&seq=trust-badge-qc-v2&orientation=landscape',
  },
  {
    icon: 'ri-guide-line',
    label: 'Safe Shopping',
    desc: 'Trusted seller recommendations',
    image: 'https://readdy.ai/api/search-image?query=Open%20guidebook%20with%20shopping%20tips%20and%20a%20small%20padlock%20symbol%20on%20a%20warm%20oak%20desk%20natural%20morning%20light%20peaceful%20secure%20atmosphere%20minimalist%20editorial%20style%20warm%20neutral%20tones%20clean%20composition%20high%20detail%20soft%20shadows&width=400&height=300&seq=trust-badge-safe-v2&orientation=landscape',
  },
  {
    icon: 'ri-group-line',
    label: 'Community Picks',
    desc: 'Curated by thousands of shoppers',
    image: 'https://readdy.ai/api/search-image?query=Collection%20of%20handwritten%20review%20notes%20and%20star%20ratings%20arranged%20on%20a%20warm%20cream%20surface%20soft%20natural%20light%20from%20window%20suggesting%20community%20feedback%20and%20shared%20experiences%20minimalist%20editorial%20style%20warm%20tones%20clean%20composition%20high%20detail&width=400&height=300&seq=trust-badge-community-v2&orientation=landscape',
  },
];

export default function Home() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.beauty';

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What Is a Pandabuy Spreadsheet?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A Pandabuy Spreadsheet is a curated collection of product links organized by category that helps shoppers discover trending items on Pandabuy without manually searching through thousands of listings."
        }
      },
      {
        "@type": "Question",
        "name": "Is It Safe to Use a Pandabuy Spreadsheet?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Using a Pandabuy Spreadsheet is generally safe as long as the sheet is maintained by a trusted community curator who regularly verifies links and sellers."
        }
      },
      {
        "@type": "Question",
        "name": "How Do I Use a Pandabuy Spreadsheet to Shop?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Browse the categories you are interested in, click any product link to open it on Pandabuy, review item details and seller feedback, then add to cart and proceed with international shipping."
        }
      },
      {
        "@type": "Question",
        "name": "How Often Is the Pandabuy Spreadsheet Updated?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The Pandabuy Spreadsheet is updated daily to include the latest trending fashion finds, newly released sneaker batches, seasonal collections, and budget-friendly alternatives."
        }
      },
      {
        "@type": "Question",
        "name": "Which Categories Are Most Popular?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The most popular categories are sneakers and shoes, followed by hoodies and sweaters, t-shirts, jackets, and accessories. Budget finds under $50 and luxury brand alternatives are also highly sought-after."
        }
      }
    ]
  };

  const websiteStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Pandabuy Beauty",
    "url": siteUrl,
    "description": "Discover the best Pandabuy Spreadsheet collection with trusted sellers, popular fashion finds, quality guides, and safe shopping resources.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://kaspreadsheet.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Pandabuy Beauty",
    "url": siteUrl,
    "logo": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg",
    "description": "The curated Pandabuy Spreadsheet directory — discover trending fashion finds, trusted sellers, and smart shopping resources updated daily.",
    "sameAs": [
      "https://kaspreadsheet.com"
    ]
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteStructuredData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationStructuredData) }} />
      <Navbar />

      <main>
        <HeroSection />

        {/* Why Use Pandabuy Spreadsheet */}
        <section className="bg-background-50 py-16 md:py-20">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5 text-center">
                Why Use a Pandabuy Spreadsheet?
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6 text-center max-w-3xl mx-auto">
                A <strong>Pandabuy Spreadsheet</strong> helps buyers discover popular products faster, compare prices across different sellers, and find trusted sources without hours of manual searching. Instead of scrolling through endless listings and risking unreliable purchases, shoppers can browse organized collections curated by category, brand, price range, and seasonal relevance — all verified and updated daily by the community shopping research team.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                {trustBadges.map((badge) => (
                  <div key={badge.label} className="group flex flex-col rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-default">
                    <div className="relative w-full aspect-[4/3] overflow-hidden">
                      <img
                        src={badge.image}
                        alt={badge.label}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-transparent to-transparent"></div>
                    </div>
                    <div className="px-4 pb-4 -mt-2 relative z-10">
                      <div className="flex items-center gap-2.5 mb-1">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                          <i className={`${badge.icon} text-sm`}></i>
                        </div>
                        <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{badge.label}</span>
                      </div>
                      <p className="text-xs text-foreground-500 pl-10">{badge.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Shopping Safety Section */}
        <section className="bg-background-100 py-16 md:py-20">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Shopping Safety with Pandabuy Spreadsheet
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                Using a <strong>Pandabuy Spreadsheet</strong> is one of the safest ways to shop on Pandabuy because every product link is reviewed by experienced community members. Always examine QC (Quality Check) images provided by sellers, compare feedback ratings across multiple sources, and understand international shipping timelines before placing your order. Our curated spreadsheets feature only sellers with proven track records, minimizing the risk of receiving items that do not match their descriptions or photos.
              </p>
              <Link
                to="/pandabuy-spreadsheet-guide"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                Learn more about safe shopping
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <CategoryGrid />
        <ArticleSection />
        <FAQSection />
        <CTASection />

        {/* Internal Links Section */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                Explore More Pandabuy Resources
              </h2>
              <p className="text-foreground-600 text-sm mb-6">
                Browse our complete Pandabuy Spreadsheet guide, discover category-specific finds, or visit the main site for the full collection.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/pandabuy-spreadsheet-guide"
                  className="px-4 py-2 rounded-full bg-accent-100 text-accent-800 text-sm font-medium whitespace-nowrap hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  Complete Guide
                </Link>
                <Link
                  to="/pandabuy-spreadsheet-for-beginners"
                  className="px-4 py-2 rounded-full bg-accent-100 text-accent-800 text-sm font-medium whitespace-nowrap hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  Beginners Guide
                </Link>
                <Link
                  to="/pandabuy-spreadsheet-sneakers"
                  className="px-4 py-2 rounded-full bg-background-200/70 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-300/70 transition-colors cursor-pointer"
                >
                  Sneakers Guide
                </Link>
                <Link
                  to="/budget-pandabuy-finds-under-50"
                  className="px-4 py-2 rounded-full bg-background-200/70 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-300/70 transition-colors cursor-pointer"
                >
                  Budget Finds
                </Link>
                {categories.slice(0, 6).map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-4 py-2 rounded-full bg-background-200/70 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-300/70 transition-colors cursor-pointer"
                  >
                    Pandabuy {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}